/**
 * IDI-469: deleting a work order (replaces the old Cancel action).
 *
 * woDeleteSummary(wo) is pure: it builds what the confirm modal says will be
 * lost. confirmAndDeleteWO() shows that modal through useConfirm() and, when
 * the user confirms, calls IDI-468's delete_work_order action.
 */
import React from 'react';
import api from '../services/api';
import { woSchedule, woCompletionLabel } from './woSchedule';
import { formatDate, formatDateTime } from './formatDate';

const arr = (v) => (Array.isArray(v) ? v : []);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function woDeleteSummary(wo) {
  const w = wo || {};
  const n = {
    time: arr(w.timeEntries || w.time_entries).length,
    materials: arr(w.materials).length,
    notes: arr(w.notes).length,
    files: arr(w.attachments).length,
  };
  const counts = [
    n.time && plural(n.time, 'time entry', 'time entries'),
    n.materials && plural(n.materials, 'material', 'materials'),
    n.notes && plural(n.notes, 'note', 'notes'),
    n.files && plural(n.files, 'file', 'files'),
  ].filter(Boolean);

  let whenLine = woCompletionLabel(w);
  if (!whenLine) {
    const s = woSchedule(w);
    if (s.start) whenLine = `Scheduled for ${s.hasStartTime ? formatDateTime(s.start) : formatDate(s.start)}`;
  }

  const clockedIn = !!(w.clockedInAt || w.clocked_in_at || w.openTimeEntry?.clockIn);
  const checkedOut = arr(w.resources).some(
    (r) => r && typeof r === 'object' && r.checkedOutAt && !r.checkedInAt,
  );
  const warning = clockedIn || checkedOut
    ? 'Someone is clocked in or a resource is checked out on this work order.'
    : '';

  return { counts, whenLine, warning };
}

function DeleteMessage({ summary }) {
  const { counts, whenLine, warning } = summary;
  return (
    <span className="block space-y-2" data-testid="wo-delete-summary">
      {whenLine && <span className="block">{`This work order is ${whenLine.charAt(0).toLowerCase()}${whenLine.slice(1)}.`}</span>}
      {counts.length > 0 && (
        <span className="block">{`Its ${counts.join(', ')} will be deleted with it.`}</span>
      )}
      {warning && <span className="block font-medium text-red-600">{warning}</span>}
      <span className="block">This can't be undone.</span>
    </span>
  );
}

/**
 * Returns true when the WO is gone (deleted now, or already deleted — 404),
 * false when the user kept it. Any other failure throws; err.message carries
 * the server's friendly text (409 → the job-changed reload message).
 */
export async function confirmAndDeleteWO({ confirm, wo, jobId }) {
  const ok = await confirm({
    title: 'Delete this work order?',
    message: <DeleteMessage summary={woDeleteSummary(wo)} />,
    confirmLabel: 'Delete work order',
    cancelLabel: 'Keep',
    tone: 'danger',
  });
  if (!ok) return false;
  try {
    await api.deleteWorkOrder(jobId, wo.id || wo.workOrderId);
  } catch (err) {
    if (err?.response?.status === 404) return true;
    throw err;
  }
  return true;
}
